/**
 * @Author: v_jppjpeng
 * @Date: 2023-03-29 16:33:04
 * @Explain: 分组列表
 */
import { intlGet } from '@/locales';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { Checkbox, List } from 'tdesign-react';
// utils
import { sortAsc } from '@/utils/common';
import { doesItExist, isArray, isArrayLen } from '@/utils/common/dataJudgment';
// type
import type { CommonOptionsType } from '@/definition/common/data.d';
import type { GroupListProps, SelectItemType } from '../data.d';
import { InputSizeEnum } from '../data.d';
import ListItem from '../list';
import SearchBox from '../searchBox';
import GroupItem from './groupItem';

type PropsType = {
  handleCollapseChange: () => void;
} & GroupListProps;
export default function GroupList(props: PropsType) {
  const {
    value,
    groupTitleJson,
    groupKey,
    groupSortType = 'title',
    options,
    searchPlaceholder,
    size = InputSizeEnum.M,
    searchable = true,
    multiple,
    isExpanded,
    defaultExpanded,
    autofocus,
    displayTextKey,
    emptyText = intlGet('COMMON.NO.DATA'),
    listHeader,
    groupDividingLine = false,
    customOption,
    onChange,
    onClick,
    handleCollapseChange,
  } = props;

  const [searchValue, setSearchValue] = useState<any>();

  const getGroup = (data: any[], key: string) => {
    const obj: any = {};
    data.forEach((item) => {
      const gKey = item[key];
      obj[gKey] = obj[gKey] || [];
      obj[gKey].push(item);
    });
    if (groupSortType === 'title') {
      // 按照传入标题对象顺序 - 排序
      const sortKeys = Object.keys(groupTitleJson);
      return sortKeys.reduce((acc: any, key) => {
        if (obj.hasOwnProperty(key)) {
          acc[key] = obj[key];
        }
        return acc;
      }, {});
    }
    return obj;
  };
  const dataList: Record<any, any> = useMemo(
    () => getGroup(options, groupKey),
    [options, groupKey],
  );

  // 分组按照sort字段值排序 或 按照原定方式排序
  const sortGroupKeyRes = useMemo(() => {
    const sortValue = Object.values(groupTitleJson);
    const sortKeys = Object.keys(groupTitleJson);
    const result: (number | string)[] = [];
    if (groupSortType === 'sort' && sortValue.every((item) => item?.sort)) {
      sortAsc(sortValue, 'sort');
      for (let i = 0; i < sortValue.length; i++) {
        const item = sortValue[i];
        sortKeys.map((key) => {
          const isSame = groupTitleJson[key]?.sort === item?.sort;
          if (isSame) {
            result.push(key);
          }
          return key;
        });
      }
      return result;
    }
    return Object.keys(dataList);
  }, [groupTitleJson, groupSortType, dataList]);

  /**
   * 分组标题
   * 兼容两种格式 如果是字符串则直接显示，如果是对象则以对象的方式逻辑显示
   */
  const groupTitle = useCallback(
    (key: string) => {
      const item = groupTitleJson[key];
      return item ? (
        <>
          <label className="group-title">{item.title}</label>
          {!searchValue && item?.render && item?.render()}
        </>
      ) : (
        ''
      );
    },
    [groupTitleJson, searchValue],
  );

  const onText = (item: any) => intlGet(item.labelKey) || item.label || '';
  const onTips = (item: any) => intlGet(item.tipsKey) || item.tips || '';
  const onDataTitle = (item: any) =>
    intlGet(item.dataTitleKey) || item.dataTitle || '';
  /** 重组参数 */
  const getItemProps = (item: CommonOptionsType) => {
    const newItem: any = { ...item };

    const text: any = onText(item);
    const tips: any = onTips(item);
    const dataTitle: any = onDataTitle(item);

    return {
      key: item.value,
      title: tips ? '' : dataTitle || text,
      textRender: (displayTextKey && newItem?.[displayTextKey]) || text,
      disabled: item.disabled,
      tooltip: tips,
      tooltipProps: item.tooltipProps,
      customOption: customOption
        ? () =>
            customOption?.({
              ...item,
              option: item,
            })
        : undefined,
      onClick: () => {},
    };
  };

  // 列表渲染
  const listItem = useCallback(
    (item: CommonOptionsType) => {
      const commonProps = getItemProps(item);

      if (!multiple) {
        commonProps.onClick = () => {
          if (!item.disabled) {
            // onClose?.();
            item?.onClick?.(item.value, item) || onClick?.(item.value, item);
          }
        };
      }

      return (
        <ListItem
          {...commonProps}
          key={commonProps.key}
          selected={item.value === value}
          textRender={
            multiple ? (
              <Checkbox value={item.value} disabled={item.disabled}>
                {commonProps.textRender}
              </Checkbox>
            ) : (
              commonProps.textRender
            )
          }
        />
      );
    },
    [value, multiple, onClick],
  );

  // 列表渲染
  const listRender = useCallback(
    (data: CommonOptionsType[]) => {
      const list = data.filter((item: CommonOptionsType) => {
        return searchValue
          ? doesItExist(searchValue, onText(item), true)
          : true;
      });

      return isArrayLen(list) ? (
        <List className="td-scroll-list">
          {list.map((item) => listItem(item))}
        </List>
      ) : null;
    },
    [searchValue, multiple, value],
  );

  // 是否空
  const isEmpty = useMemo(() => {
    const flagArr: boolean[] = [];
    // options内容为空 -> 暂无数据
    if (!options?.length) flagArr.push(false);

    if (searchValue) {
      sortGroupKeyRes.forEach((key) => {
        const children = dataList[key] || [];

        // some 用于检测数组中所有元素是否都符合指定条件
        // 如果有一个元素满足条件，则表达式返回true, 剩余的元素不会再执行检测。
        // 如果没有满足条件的元素，则返回false。
        flagArr.push(
          children.some((item: SelectItemType) => {
            return doesItExist(searchValue, onText(item), true);
          }),
        );
      });
    }

    // every 用于检测数组中的所有元素是否都满足指定条件
    // 如果全都满足, 则返回true
    // 如果有有一项不满足条件, 则表达式返回false
    return isArrayLen(flagArr) ? flagArr.every((item) => !item) : false;
  }, [searchValue, options, dataList, sortGroupKeyRes]);

  // 手动更新弹层位置(折叠项展开/收起时)
  const onHandleCollapseChange = useCallback(() => {
    handleCollapseChange?.();
  }, [handleCollapseChange, sortGroupKeyRes]);

  return (
    <>
      {/* 搜索框 */}
      {searchable && (
        <div className="dropdown-search-box pd-10">
          <SearchBox
            autofocus={autofocus}
            size="full"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(val) => {
              setSearchValue(val);
            }}
          />
        </div>
      )}

      {/* 列表 - 头部 */}
      {listHeader}

      {/* 列表 */}
      {isEmpty ? (
        <div className="td-popup-empty-text">{emptyText}</div>
      ) : (
        <Checkbox.Group
          className={classNames('td-group-list', {
            'td-group-checkbox-list': multiple,
            'td-group-expanded-list': isExpanded,
            'td-group-dividing-line': groupDividingLine, // 分组分割线
            'mt-0': searchable || listHeader,
            [`size-${size}`]: size,
          })}
          value={value}
          onChange={onChange}
        >
          <ul className="width-100">
            {sortGroupKeyRes.map((key) => {
              const children = isArray(dataList[key])
                ? listRender(dataList[key])
                : undefined;

              return (
                children && (
                  <li key={key} onClick={(e) => e.stopPropagation()}>
                    <GroupItem
                      isExpanded={isExpanded}
                      defaultExpanded={defaultExpanded}
                      title={groupTitleJson ? groupTitle(key) : ''}
                      onClick={onHandleCollapseChange}
                    >
                      {children}
                    </GroupItem>
                  </li>
                )
              );
            })}
          </ul>
        </Checkbox.Group>
      )}
    </>
  );
}
